// @flow

import { Device, Call } from '@twilio/voice-sdk';

type CallStatusCallback = (status: string) => void;

type ConnectParams = {
  To: string,
  ConversationId: string,
  MessageId: string,
  FromNumber: string
};

/**
 * VoiceService - Singleton service for managing Twilio Voice SDK browser calls
 *
 * Usage:
 * 1. Initialize with access token: VoiceService.initialize(token)
 * 2. Make a call: VoiceService.connect(params)
 * 3. End call: VoiceService.hangup()
 * 4. Clean up: VoiceService.destroy()
 */
class VoiceServiceClass {
  device: ?Device = null;
  currentCall: ?Call = null;
  onStatusChange: ?CallStatusCallback = null;
  isInitialized: boolean = false;

  /**
   * Initialize the Twilio Voice Device with an access token
   * @param {string} token - Twilio access token from backend
   */
  async initialize(token: string): Promise<void> {
    console.log('[VoiceService] initializing device');

    // Clean up existing device if any
    if (this.device) {
      this.device.destroy();
    }

    this.device = new Device(token, {
      logLevel: 1, // 0=silent, 1=error, 2=warn, 3=info, 4=debug
      codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      allowIncomingWhileBusy: false
    });

    // Set up device event listeners
    this.device.on('registered', () => {
      console.log('[VoiceService] device registered');
      this.isInitialized = true;
    });

    this.device.on('unregistered', () => {
      console.log('[VoiceService] device unregistered');
      this.isInitialized = false;
    });

    this.device.on('error', (error) => {
      console.error('[VoiceService] device error:', error);
      this.onStatusChange?.('error', error);
    });

    this.device.on('tokenWillExpire', () => {
      console.warn('[VoiceService] token will expire - should refresh');
      // TODO: Implement token refresh
    });

    // Register the device
    await this.device.register();
    console.log('[VoiceService] device registration complete');
  }

  /**
   * Make an outbound call
   * @param {ConnectParams} params - Call parameters
   * @returns {Promise<Call>} The active call
   */
  async connect(params: ConnectParams): Promise<Call> {
    if (!this.device) {
      throw new Error('Voice device not initialized. Call initialize() first.');
    }

    console.log('[VoiceService] connecting call to:', params.To);

    // Connect to Twilio - this will trigger the TwiML webhook
    this.currentCall = await this.device.connect({
      params: {
        To: params.To,
        ConversationId: params.ConversationId,
        MessageId: params.MessageId,
        FromNumber: params.FromNumber
      }
    });

    // Set up call event listeners
    this.currentCall.on('ringing', () => {
      console.log('[VoiceService] call ringing');
      this.onStatusChange?.('ringing');
    });

    this.currentCall.on('accept', () => {
      console.log('[VoiceService] call accepted (in-progress)');
      this.onStatusChange?.('in-progress');
    });

    this.currentCall.on('disconnect', (call) => {
      console.log('[VoiceService] call disconnected');
      this.onStatusChange?.('completed');
      this.currentCall = null;
    });

    this.currentCall.on('cancel', () => {
      console.log('[VoiceService] call cancelled');
      this.onStatusChange?.('cancelled');
      this.currentCall = null;
    });

    this.currentCall.on('reject', () => {
      console.log('[VoiceService] call rejected');
      this.onStatusChange?.('rejected');
      this.currentCall = null;
    });

    this.currentCall.on('error', (error) => {
      console.error('[VoiceService] call error:', error);
      this.onStatusChange?.('failed');
      this.currentCall = null;
    });

    return this.currentCall;
  }

  /**
   * Hang up the current call
   */
  hangup(): void {
    if (this.currentCall) {
      console.log('[VoiceService] hanging up call');
      this.currentCall.disconnect();
      this.currentCall = null;
    }
  }

  /**
   * Mute or unmute the current call
   * @param {boolean} shouldMute - True to mute, false to unmute
   */
  mute(shouldMute: boolean): void {
    if (this.currentCall) {
      this.currentCall.mute(shouldMute);
      console.log('[VoiceService] call muted:', shouldMute);
    }
  }

  /**
   * Check if currently muted
   * @returns {boolean} True if muted
   */
  isMuted(): boolean {
    return this.currentCall?.isMuted() ?? false;
  }

  /**
   * Get the current call status
   * @returns {?string} Call status or null if no active call
   */
  getCallStatus(): ?string {
    if (!this.currentCall) return null;
    return this.currentCall.status();
  }

  /**
   * Check if there's an active call
   * @returns {boolean} True if there's an active call
   */
  hasActiveCall(): boolean {
    return this.currentCall !== null;
  }

  /**
   * Clean up and destroy the device
   */
  destroy(): void {
    console.log('[VoiceService] destroying device');
    if (this.currentCall) {
      this.currentCall.disconnect();
      this.currentCall = null;
    }
    if (this.device) {
      this.device.destroy();
      this.device = null;
    }
    this.isInitialized = false;
    this.onStatusChange = null;
  }

  /**
   * Set the status change callback
   * @param {CallStatusCallback} callback - Function to call on status changes
   */
  setStatusCallback(callback: CallStatusCallback): void {
    this.onStatusChange = callback;
  }
}

// Export as singleton
const VoiceService = new VoiceServiceClass();
export default VoiceService;
